import { endOfDay, isToday, format, parse, startOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDateFns } from '~/Hooks';
import { IComboboxOptions } from '~/UIKit/Form/ComboBox';
import { useRequestEvents } from '~/requests/eventService';
import { selectTrainersMap, useLocations } from '~/store/provider';

interface IUseManageBookingsModal {
  eventId: number;
  setEventId: (eventId: number) => void;
  emailOrMemberId: string;
  setEmailOrMemberId: (emailOrMemberId: string) => void;
}

export const useManageBookingsConfirmationModal = ({
  eventId,
  emailOrMemberId,
  setEventId,
  setEmailOrMemberId,
}: IUseManageBookingsModal) => {
  const { convertToProviderTimezone, currentLocale } = useDateFns();
  const [eventDate, setEventDate] = useState(new Date());
  const [completeEvents, setCompleteEvents] = useState<IComboboxOptions[]>([]);
  const trainersMap = useSelector(selectTrainersMap);
  const [formattedDate, setFormattedDate] = useState('');
  const { locationList } = useLocations();
  const result = useRequestEvents({
    archived: false,
    from: convertToProviderTimezone(startOfDay(eventDate).toISOString()).toISOString(),
    to: convertToProviderTimezone(endOfDay(eventDate).toISOString()).toISOString(),
    size: 1000,
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const addLocationNamesToEventList = () =>
    result.data?.map((event) => {
      const location = locationList?.find((loc) => loc.uuid === event.locationId);
      const trainers = event.trainerIds.map((trainerId) => trainersMap[trainerId]?.name);

      if (location && trainers) {
        return {
          ...event,
          locationName: location.name,
          trainerNames: trainers,
        };
      }
      return event;
    });

  useEffect(() => {
    const newEvents = addLocationNamesToEventList();
    if (newEvents !== undefined) {
      setCompleteEvents(newEvents);
    }
  }, [result.data, locationList, trainersMap]);

  useEffect(() => {
    if (eventId !== 0 && emailOrMemberId !== '') {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [eventId, emailOrMemberId]);

  const onArrowClick = (direction: 'left' | 'right') => {
    clearInput();
    if (direction === 'right' && !isToday(eventDate)) {
      setEventDate((prevDate) => {
        const newDate = new Date(prevDate);
        newDate.setDate(newDate.getDate() + 1);
        return newDate;
      });
    } else {
      setEventDate((prevDate) => {
        const newDate = new Date(prevDate);
        newDate.setDate(newDate.getDate() - 1);
        return newDate;
      });
    }
  };

  useEffect(() => {
    clearInput();
  }, [eventDate]);

  const clearInput = () => {
    setSelectedOption(null);
    setEventId(0);
  };

  useEffect(() => {
    const date = eventDate.toLocaleDateString('en-GB');
    const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
    setFormattedDate(format(parsedDate, 'dd MMM yyyy'));
  }, [currentLocale, eventDate]);

  const handleMembershipOrEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrMemberId(event.target.value);
  };

  const handleDropdownChange = (event: React.SyntheticEvent, value: any) => {
    setSelectedOption(value);
    value ? setEventId(value.id) : setEventId(0);
  };

  return {
    events: completeEvents,
    isLoading: result?.isLoading,
    eventDate,
    onArrowClick,
    setIsFormValid,
    isFormValid,
    setEmailOrMemberId,
    emailOrMemberId,
    eventId,
    setEventId,
    handleMembershipOrEmailInputChange,
    handleDropdownChange,
    selectedOption,
    formattedDate,
  };
};
